import { useEffect, useRef, useState } from "react";
import { api, type Message, type NotifyLevel } from "../api";
import { useShallow } from "zustand/shallow";
import { go, useStore } from "../store";
import { plain, timeLabel } from "../text";
import { Avatar, toast } from "./ui";
import { Icon } from "./icons";

/** ↑ in an empty box: edit your last message in this chat (or thread). */
export function editLast(list: Message[], meId: number) {
  const last = [...list].reverse().find((m) => m.userId === meId && !m.deletedAt);
  if (last) useStore.setState({ editingId: last.id });
}

function usePopover(onClose: () => void) {
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const c = (e: MouseEvent) => !box.current?.contains(e.target as Node) && onClose();
    const k = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    setTimeout(() => addEventListener("mousedown", c)); addEventListener("keydown", k);
    return () => { removeEventListener("mousedown", c); removeEventListener("keydown", k); };
  }, [onClose]);
  return box;
}

const LEVELS: [NotifyLevel, string, string][] = [
  ["all", "Every message", "Notify and count every new message."],
  ["mentions", "Only @mentions", "Bold when there's something new, but only @you notifies."],
  ["none", "Mute", "Nothing at all; @mentions still reach you."],
];

export function NotifyMenu({ roomId }: { roomId: number }) {
  const level = useStore((s) => s.states[roomId]?.notify ?? "all");
  const own = useStore((s) => s.states[roomId]?.own ?? null);
  const [open, setOpen] = useState(false);
  const box = usePopover(() => setOpen(false));
  const pick = async (l: NotifyLevel | "default") => {
    setOpen(false);
    const prev = useStore.getState().states[roomId];
    if (l !== "default") useStore.setState((s) => ({ states: { ...s.states, [roomId]: { ...s.states[roomId], notify: l, own: l } } }));
    try { const st = await api.setNotify(roomId, l); useStore.setState((s) => ({ states: { ...s.states, [roomId]: st } })); }
    catch (e: any) { useStore.setState((s) => ({ states: { ...s.states, [roomId]: prev } })); toast(e.message || "Couldn't change it"); }
  };
  return (
    <span className="hpop">
      <button className="hb" title="Notifications for this chat" onClick={() => setOpen((o) => !o)}>
        <Icon n={level === "all" ? "bell" : level === "mentions" ? "at" : "belloff"} s={14} />{level === "all" ? "All messages" : level === "mentions" ? "@mentions" : "Muted"}
      </button>
      {open && (
        <div className="menu down" ref={box}>
          <h6>Notify me about</h6>
          {LEVELS.map(([l, t, d]) => <button key={l} className={`mi${l === level ? " on" : ""}`} onClick={() => pick(l)}><Icon n={l === "all" ? "bell" : l === "mentions" ? "at" : "belloff"} /><span>{t}<small>{d}</small></span>{l === level && <kbd>✓</kbd>}</button>)}
          {own && <><hr /><button className="mi" onClick={() => pick("default")}><Icon n="x" /><span>Use my default<small>Set in your profile card.</small></span></button></>}
        </div>
      )}
    </span>
  );
}

export function PinsButton({ roomId }: { roomId: number }) {
  const pins = useStore(useShallow((s) => s.pins.filter((p) => p.roomId === roomId)));
  const byId = useStore((s) => s.byId);
  const users = useStore((s) => s.users);
  const [open, setOpen] = useState(false);
  const box = usePopover(() => setOpen(false));
  useEffect(() => {
    // Load pinned messages we haven't seen yet (older than the loaded page).
    if (open) for (const p of pins) if (!byId[p.messageId]) api.thread(p.messageId).then((t) => useStore.setState((s) => ({ byId: { ...s.byId, [t.root.id]: t.root, ...Object.fromEntries(t.replies.map((r) => [r.id, r])) } }))).catch(() => {});
  }, [open]);
  if (!pins.length) return null;
  return (
    <span className="hpop">
      <button className="hb" title="Pinned messages" onClick={() => setOpen((o) => !o)}><Icon n="pin" s={14} />{pins.length} pinned</button>
      {open && (
        <div className="menu down wide" ref={box}>
          <h6>Pinned</h6>
          {pins.map((p) => { const m = byId[p.messageId]; return (
            <button key={p.messageId} className="pinrow" onClick={() => { setOpen(false); go({ v: "room", id: roomId, focus: p.messageId, thread: m?.parentId ?? undefined }); }}>
              <span className="pw"><Avatar user={m ? users[m.userId] : undefined} size={18} /><b>{m ? users[m.userId]?.name : "…"}</b><small>{m ? timeLabel(m.createdAt) : ""}</small></span>
              <span className="pt">{m ? plain(m.body).slice(0, 140) || "📎 file" : "Loading…"}</span>
            </button>
          ); })}
        </div>
      )}
    </span>
  );
}

/** "Hannah is typing…" under the message box. */
export function Typing({ roomId, parentId }: { roomId: number; parentId: number | null }) {
  const typing = useStore((s) => s.typing[`${roomId}:${parentId ?? 0}`]);
  const users = useStore((s) => s.users);
  const [, tick] = useState(0);
  useEffect(() => { const t = setInterval(() => tick((x) => x + 1), 1000); return () => clearInterval(t); }, []);
  const who = Object.entries(typing ?? {}).filter(([, until]) => until > Date.now()).map(([u]) => users[+u]?.name).filter(Boolean);
  return (
    <div className="typing">{who.length > 0 && <><span className="dots"><i /><i /><i /></span>{who.length === 1 ? `${who[0]} is typing…` : who.length === 2 ? `${who[0]} and ${who[1]} are typing…` : "Several people are typing…"}</>}</div>
  );
}

/** Your scheduled messages for this chat, above the box: change the time, send now or cancel. */
export function ScheduledBar({ roomId, parentId }: { roomId: number; parentId: number | null }) {
  const list = useStore(useShallow((s) => s.scheduled.filter((x) => x.roomId === roomId && (x.parentId ?? null) === parentId && (x.status === "waiting" || x.status === "failed"))));
  const [open, setOpen] = useState(false);
  if (!list.length) return null;
  const edit = async (id: number, p: { sendAt?: string; cancel?: boolean; now?: boolean }) => {
    try { useStore.setState({ scheduled: await api.editScheduled(id, p) }); } catch (e: any) { toast(e.message || "Couldn't change it"); }
  };
  return (
    <div className="sched">
      <button className="spill" onClick={() => setOpen((o) => !o)}><Icon n="clock" s={13} />{list.length} scheduled {list.length === 1 ? "message" : "messages"} · {new Date(list[0].sendAt).toLocaleString("en-GB", { weekday: "short", hour: "2-digit", minute: "2-digit" })}<Icon n="chev" s={11} /></button>
      {open && list.map((x) => (
        <div key={x.id} className={`srow${x.status === "failed" ? " bad" : ""}`}>
          <span className="st">{x.status === "failed" ? `Failed: ${x.error}` : new Date(x.sendAt).toLocaleString("en-GB", { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
          <span className="sb">{plain(x.body).slice(0, 90)}</span>
          <button onClick={() => edit(x.id, { now: true })}>Send now</button>
          <button onClick={() => edit(x.id, { cancel: true })}>Cancel</button>
        </div>
      ))}
    </div>
  );
}
